/**
 * The only `PlatformApiClient` implementation wired up in this app today (`src/main.tsx`).
 * An in-memory fixture store with simulated network latency — no `fetch`, no backend
 * connectivity, ever. See `PlatformApiClient.ts`'s doc comment for the swap-in contract a
 * real implementation must satisfy, and this app's ARCHITECTURE.md for the full "why" (no
 * `PlatformOperator` model / `aud: "platform"` login route / `platform_audit_log` table /
 * operator-facing tenant endpoints exist in `backend/` yet).
 *
 * State lives in module-level `let`/`Map` variables, reset on every page reload — this is
 * a demo data layer, not a persistence layer, and pretending otherwise (e.g. writing to
 * `localStorage`) would misrepresent it as more durable than it is.
 */
import { asDecimalString } from "@shop-ledger/shared-types";
import { newId } from "../lib/id";
import type { PlatformApiClient } from "./PlatformApiClient";
import { buildMockHealthNodes } from "./mockHealthNodes";
import { countAgedBeyondThreshold, DEFAULT_STALE_THRESHOLD_MS } from "../domain/queueAge";
import {
  PLAN_DEFAULTS,
  type BackupTrigger,
  type DemoResetResult,
  type ImpersonationSession,
  type LiveOnboardingChecklist,
  type LiveOnboardingChecklistStep,
  type LogEntry,
  type LogFilter,
  type MarketplaceOrderFilters,
  type MarketplaceOrderLineItem,
  type MarketplaceOrderRollupStatus,
  type MarketplaceSuborderStatusUpdateInput,
  type MfaChallenge,
  type OnboardingChecklist,
  type OnboardingSubmission,
  type PaymentReconciliationQueueItem,
  type PlanCode,
  type PlatformAuditLogEntry,
  type PlatformGeneralSettings,
  type PlatformGeneralSettingsUpdate,
  type PlatformMarketplaceOrderDetail,
  type PlatformMarketplaceOrderSummary,
  type PlatformMarketplaceSuborderDetail,
  type PlatformSession,
  type PlatformStats,
  type PlatformTenantDetail,
  type PlatformTenantSummary,
  type QueueAlertSummary,
  type SendTestEmailResult,
  type ShipmentStatus,
  type StorageQuotaWarning,
  type SyncConflictQueueItem,
  type TenantCreationResult,
} from "./types";

/**
 * `api/types.ts` widened `PlatformTenantDetail.plan_code`/`usage.storage_*_bytes`/
 * `checklist` to include `null` so `realPlatformApiClient.ts` can honestly represent
 * "the real backend has no data for this field" (see that file's doc comment). This mock
 * client, unlike the real one, ALWAYS has a concrete value for every one of those fields —
 * so its internal seed/working data is typed with those fields non-nullable. A
 * `MockTenantDetail` is still a valid `PlatformTenantDetail` (a narrower type is assignable
 * to the wider `T | null` union), so every method below can return one directly without a
 * cast; this just keeps the mock's own internals free of null-checks for values it never
 * actually leaves null. */
interface MockTenantDetail
  extends Omit<PlatformTenantDetail, "plan_code" | "usage" | "checklist" | "onboarding_complete"> {
  plan_code: PlanCode;
  usage: {
    branch_count: number;
    active_users: number;
    storage_used_bytes: number;
    storage_quota_bytes: number;
    sync_events_last_30d: number;
  };
  checklist: OnboardingChecklist;
}

/** Simulated network latency — small enough to keep the test suite fast, large enough
 * that every page's loading state is genuinely exercised in the running app rather than
 * resolving before a single frame paints. */
function delay(ms = 120): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function minutesAgo(n: number): string {
  return new Date(Date.now() - n * 60_000).toISOString();
}

function hoursAgo(n: number): string {
  return new Date(Date.now() - n * 60 * 60_000).toISOString();
}

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 24 * 60 * 60_000).toISOString();
}

// ---------------------------------------------------------------------------------
// Seed data
// ---------------------------------------------------------------------------------

interface OperatorRecord {
  id: string;
  email: string;
  password: string;
  display_name: string;
  mfa_enrolled: boolean;
}

/** Two seeded operator accounts — documented in the login page itself (not hidden), since
 * this is a demo credential set, not a real secret. */
const OPERATORS: OperatorRecord[] = [
  {
    id: "op-0001-alex",
    email: "operator@shopledger.dev",
    password: "platform-demo",
    display_name: "Alex Operator",
    mfa_enrolled: true,
  },
  {
    id: "op-0002-priya",
    email: "priya@shopledger.dev",
    password: "platform-demo",
    display_name: "Priya Operator",
    mfa_enrolled: true,
  },
];

const TENANT_IDS = {
  acme: "10000000-0000-4000-8000-000000000001",
  bluewave: "10000000-0000-4000-8000-000000000002",
  citrus: "10000000-0000-4000-8000-000000000003",
  desert: "10000000-0000-4000-8000-000000000004",
  everline: "10000000-0000-4000-8000-000000000005",
  falcon: "10000000-0000-4000-8000-000000000006",
} as const;

const GB = 1024 ** 3;

const seedTenants: MockTenantDetail[] = [
  {
    id: TENANT_IDS.acme,
    business_name: "Acme Pharmacy",
    business_type: "PHARMACY",
    currency: "UGX",
    is_suspended: false,
    is_demo: false,
    plan_code: "GROWTH",
    created_at: daysAgo(180),
    last_transaction_at: hoursAgo(3),
    turnover_all_time: asDecimalString("184320000"),
    turnover_last_30d: asDecimalString("9840000"),
    usage: {
      branch_count: 4,
      active_users: 11,
      storage_used_bytes: Math.round(41.5 * GB),
      storage_quota_bytes: 50 * GB,
      sync_events_last_30d: 18420,
    },
    checklist: {
      tenant_id: TENANT_IDS.acme,
      first_branch_created: true,
      first_product_added: true,
      first_sale_recorded: true,
      updated_at: daysAgo(179),
    },
  },
  {
    id: TENANT_IDS.bluewave,
    business_name: "Bluewave Supermarket",
    business_type: "SUPERMARKET",
    currency: "KES",
    is_suspended: false,
    is_demo: false,
    plan_code: "ENTERPRISE",
    created_at: daysAgo(365),
    last_transaction_at: hoursAgo(1),
    turnover_all_time: asDecimalString("1042650000"),
    turnover_last_30d: asDecimalString("58230000"),
    usage: {
      branch_count: 12,
      active_users: 54,
      storage_used_bytes: Math.round(210 * GB),
      storage_quota_bytes: 500 * GB,
      sync_events_last_30d: 96210,
    },
    checklist: {
      tenant_id: TENANT_IDS.bluewave,
      first_branch_created: true,
      first_product_added: true,
      first_sale_recorded: true,
      updated_at: daysAgo(364),
    },
  },
  {
    id: TENANT_IDS.citrus,
    business_name: "Citrus Retail Co.",
    business_type: "RETAIL",
    currency: "UGX",
    is_suspended: true,
    is_demo: false,
    plan_code: "STARTER",
    created_at: daysAgo(90),
    last_transaction_at: daysAgo(60),
    turnover_all_time: asDecimalString("8420000"),
    turnover_last_30d: asDecimalString("0"),
    usage: {
      branch_count: 2,
      active_users: 5,
      storage_used_bytes: Math.round(3.2 * GB),
      storage_quota_bytes: 10 * GB,
      sync_events_last_30d: 640,
    },
    checklist: {
      tenant_id: TENANT_IDS.citrus,
      first_branch_created: true,
      first_product_added: true,
      first_sale_recorded: false,
      updated_at: daysAgo(60),
    },
  },
  {
    id: TENANT_IDS.desert,
    business_name: "Desert Agro Supplies",
    business_type: "AGRI",
    currency: "UGX",
    is_suspended: false,
    is_demo: false,
    plan_code: "STARTER",
    created_at: daysAgo(9.4),
    last_transaction_at: hoursAgo(20),
    turnover_all_time: asDecimalString("612000"),
    turnover_last_30d: asDecimalString("612000"),
    usage: {
      branch_count: 1,
      active_users: 2,
      storage_used_bytes: Math.round(0.4 * GB),
      storage_quota_bytes: 10 * GB,
      sync_events_last_30d: 85,
    },
    checklist: {
      tenant_id: TENANT_IDS.desert,
      first_branch_created: true,
      first_product_added: false,
      first_sale_recorded: false,
      updated_at: daysAgo(9),
    },
  },
  {
    id: TENANT_IDS.everline,
    business_name: "Everline Pharmacy Group",
    business_type: "PHARMACY",
    currency: "TZS",
    is_suspended: false,
    is_demo: false,
    plan_code: "GROWTH",
    created_at: daysAgo(45),
    last_transaction_at: daysAgo(3),
    turnover_all_time: asDecimalString("276900000"),
    turnover_last_30d: asDecimalString("14200000"),
    usage: {
      branch_count: 6,
      active_users: 20,
      storage_used_bytes: Math.round(48.9 * GB),
      storage_quota_bytes: 50 * GB,
      sync_events_last_30d: 21030,
    },
    checklist: {
      tenant_id: TENANT_IDS.everline,
      first_branch_created: true,
      first_product_added: true,
      first_sale_recorded: true,
      updated_at: daysAgo(44),
    },
  },
  {
    id: TENANT_IDS.falcon,
    business_name: "Falcon Retail Trial",
    business_type: "RETAIL",
    currency: "USD",
    is_suspended: false,
    is_demo: true,
    plan_code: "TRIAL",
    created_at: hoursAgo(6),
    last_transaction_at: null,
    turnover_all_time: asDecimalString("0"),
    turnover_last_30d: asDecimalString("0"),
    usage: {
      branch_count: 0,
      active_users: 1,
      storage_used_bytes: 0,
      storage_quota_bytes: 1 * GB,
      sync_events_last_30d: 0,
    },
    checklist: {
      tenant_id: TENANT_IDS.falcon,
      first_branch_created: false,
      first_product_added: false,
      first_sale_recorded: false,
      updated_at: hoursAgo(6),
    },
  },
];

const tenants = new Map<string, MockTenantDetail>(seedTenants.map((t) => [t.id, t]));

/** §9.7 General Settings mock state — the password itself lives only here (never returned
 * from `getGeneralSettings`, matching the real backend's `smtp_password_set`-only
 * contract), reset on reload like every other fixture in this file. */
let generalSettings: {
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_username: string | null;
  smtp_password: string | null;
  smtp_from_email: string | null;
  smtp_from_name: string | null;
  smtp_use_tls: boolean;
} = {
  smtp_host: null,
  smtp_port: null,
  smtp_username: null,
  smtp_password: null,
  smtp_from_email: null,
  smtp_from_name: null,
  smtp_use_tls: true,
};

const syncConflicts: SyncConflictQueueItem[] = [
  {
    id: newId(),
    tenant_id: TENANT_IDS.acme,
    tenant_name: "Acme Pharmacy",
    entity_type: "STOCK_ADJUST",
    entity_id: newId(),
    status: "NEGATIVE_STOCK",
    created_at: minutesAgo(42),
  },
  {
    id: newId(),
    tenant_id: TENANT_IDS.bluewave,
    tenant_name: "Bluewave Supermarket",
    entity_type: "STOCK_ADJUST",
    entity_id: newId(),
    status: "NEGATIVE_STOCK",
    created_at: minutesAgo(4),
  },
  {
    id: newId(),
    tenant_id: TENANT_IDS.everline,
    tenant_name: "Everline Pharmacy Group",
    entity_type: "SALE",
    entity_id: newId(),
    status: "PROCESSING_ERROR",
    created_at: hoursAgo(3),
  },
  {
    id: newId(),
    tenant_id: TENANT_IDS.citrus,
    tenant_name: "Citrus Retail Co.",
    entity_type: "STOCK_ADJUST",
    entity_id: newId(),
    status: "NEGATIVE_STOCK",
    created_at: minutesAgo(9),
  },
];

const pendingReconciliations: PaymentReconciliationQueueItem[] = [
  {
    id: newId(),
    tenant_id: TENANT_IDS.acme,
    tenant_name: "Acme Pharmacy",
    sale_or_invoice_id: newId(),
    amount: asDecimalString("184500.00"),
    status: "PENDING",
    created_at: hoursAgo(2),
  },
  {
    id: newId(),
    tenant_id: TENANT_IDS.bluewave,
    tenant_name: "Bluewave Supermarket",
    sale_or_invoice_id: newId(),
    amount: asDecimalString("52000.00"),
    status: "PENDING",
    created_at: minutesAgo(6),
  },
  {
    id: newId(),
    tenant_id: TENANT_IDS.everline,
    tenant_name: "Everline Pharmacy Group",
    sale_or_invoice_id: newId(),
    amount: asDecimalString("973200.00"),
    status: "PENDING",
    created_at: daysAgo(1.2),
  },
];

const auditLog: PlatformAuditLogEntry[] = [
  {
    id: newId(),
    operator_id: OPERATORS[0]!.id,
    operator_email: OPERATORS[0]!.email,
    action: "TENANT_CREATED",
    target_tenant_id: TENANT_IDS.falcon,
    timestamp: hoursAgo(6),
    detail: "Onboarded via guided wizard, plan TRIAL.",
  },
];

// ---------------------------------------------------------------------------------
// §16 — unified cross-tenant marketplace orders. Mirrors the real backend's "live query,
// no desync" shape (`marketplace_service.load_suborder_details`/`compute_rollup_status`):
// a suborder's status/tracking lives on its own mock `shipment` object (this mock's stand-in
// for the real `Shipment` row), never duplicated onto the order — every read recomputes the
// rollup from current shipment statuses, exactly like the real route does.
// ---------------------------------------------------------------------------------

interface MockMarketplaceShipment {
  status: ShipmentStatus;
  carrier: string;
  tracking_number: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
}

interface MockMarketplaceSuborder {
  suborder_id: string;
  shipment_id: string;
  tenant_id: string;
  tenant_name: string;
  sale_id: string;
  sub_total: string;
  grand_total: string;
  items: MarketplaceOrderLineItem[];
  shipment: MockMarketplaceShipment;
}

interface MockMarketplaceOrder {
  id: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  region: string | null;
  postal_code: string | null;
  country: string;
  grand_total: string;
  currency: string | null;
  created_at: string;
  suborders: MockMarketplaceSuborder[];
}

/** Mirrors `backend/app/services/marketplace_service.py::compute_rollup_status` exactly
 * (read directly, not guessed) — kept in lockstep with that function's doc comment rather
 * than reimplemented independently, so the mock and real clients can never present a
 * different rollup for the same set of shipment statuses. */
function computeRollupStatus(shipmentStatuses: ShipmentStatus[]): MarketplaceOrderRollupStatus {
  if (shipmentStatuses.length === 0) return "PENDING";
  if (shipmentStatuses.every((s) => s === "DELIVERED")) return "DELIVERED";
  if (shipmentStatuses.some((s) => s === "FAILED")) return "FAILED";
  if (shipmentStatuses.every((s) => s === "PENDING")) return "PENDING";
  return "PROCESSING";
}

/** Mirrors `backend/app/services/shipping_service.py::VALID_TRANSITIONS` exactly — a
 * forward-only pipeline plus a terminal FAILED escape hatch from any non-terminal state. */
const VALID_SHIPMENT_TRANSITIONS: Record<ShipmentStatus, ReadonlySet<ShipmentStatus>> = {
  PENDING: new Set(["PACKED", "SHIPPED", "FAILED"]),
  PACKED: new Set(["SHIPPED", "FAILED"]),
  SHIPPED: new Set(["DELIVERED", "FAILED"]),
  DELIVERED: new Set(),
  FAILED: new Set(),
};

const MARKETPLACE_ORDER_IDS = {
  first: "20000000-0000-4000-8000-000000000001",
  second: "20000000-0000-4000-8000-000000000002",
} as const;

const marketplaceOrders = new Map<string, MockMarketplaceOrder>([
  [
    MARKETPLACE_ORDER_IDS.first,
    {
      id: MARKETPLACE_ORDER_IDS.first,
      contact_name: "Grace Nakato",
      contact_email: "grace.nakato@example.com",
      contact_phone: "+256700111222",
      address_line1: "Plot 14 Kira Road",
      address_line2: null,
      city: "Kampala",
      region: null,
      postal_code: null,
      country: "UG",
      grand_total: asDecimalString("94500.00"),
      currency: "UGX",
      created_at: hoursAgo(5),
      suborders: [
        {
          suborder_id: newId(),
          shipment_id: newId(),
          tenant_id: TENANT_IDS.acme,
          tenant_name: "Acme Pharmacy",
          sale_id: newId(),
          sub_total: asDecimalString("42000.00"),
          grand_total: asDecimalString("47000.00"),
          items: [
            {
              inventory_item_id: newId(),
              name: "Paracetamol 500mg (20-pack)",
              quantity: "2",
              unit_price: asDecimalString("21000.00"),
              total_price: asDecimalString("42000.00"),
            },
          ],
          shipment: { status: "PACKED", carrier: "Local courier", tracking_number: null, shipped_at: null, delivered_at: null },
        },
        {
          suborder_id: newId(),
          shipment_id: newId(),
          tenant_id: TENANT_IDS.bluewave,
          tenant_name: "Bluewave Supermarket",
          sale_id: newId(),
          sub_total: asDecimalString("42500.00"),
          grand_total: asDecimalString("47500.00"),
          items: [
            {
              inventory_item_id: newId(),
              name: "5kg rice bag",
              quantity: "1",
              unit_price: asDecimalString("42500.00"),
              total_price: asDecimalString("42500.00"),
            },
          ],
          shipment: {
            status: "SHIPPED",
            carrier: "SpeedEx",
            tracking_number: "SPX-8842-UG",
            shipped_at: hoursAgo(1),
            delivered_at: null,
          },
        },
      ],
    },
  ],
  [
    MARKETPLACE_ORDER_IDS.second,
    {
      id: MARKETPLACE_ORDER_IDS.second,
      contact_name: "Kevin Odongo",
      contact_email: "kevin.odongo@example.com",
      contact_phone: "+256700333444",
      address_line1: "9 Acacia Avenue",
      address_line2: "Apt 3B",
      city: "Entebbe",
      region: null,
      postal_code: null,
      country: "UG",
      grand_total: asDecimalString("18900.00"),
      currency: "UGX",
      created_at: daysAgo(3),
      suborders: [
        {
          suborder_id: newId(),
          shipment_id: newId(),
          tenant_id: TENANT_IDS.everline,
          tenant_name: "Everline Pharmacy Group",
          sale_id: newId(),
          sub_total: asDecimalString("16900.00"),
          grand_total: asDecimalString("18900.00"),
          items: [
            {
              inventory_item_id: newId(),
              name: "Vitamin C effervescent tablets",
              quantity: "1",
              unit_price: asDecimalString("16900.00"),
              total_price: asDecimalString("16900.00"),
            },
          ],
          shipment: {
            status: "DELIVERED",
            carrier: "SpeedEx",
            tracking_number: "SPX-7710-UG",
            shipped_at: daysAgo(2),
            delivered_at: daysAgo(1),
          },
        },
      ],
    },
  ],
]);

function toMarketplaceOrderSummary(order: MockMarketplaceOrder): PlatformMarketplaceOrderSummary {
  return {
    id: order.id,
    contact_name: order.contact_name,
    contact_email: order.contact_email,
    grand_total: order.grand_total,
    currency: order.currency,
    created_at: order.created_at,
    status: computeRollupStatus(order.suborders.map((s) => s.shipment.status)),
    tenant_count: order.suborders.length,
  };
}

function toSuborderDetail(suborder: MockMarketplaceSuborder): PlatformMarketplaceSuborderDetail {
  return {
    tenant_id: suborder.tenant_id,
    tenant_name: suborder.tenant_name,
    sale_id: suborder.sale_id,
    suborder_id: suborder.suborder_id,
    shipment_id: suborder.shipment_id,
    sub_total: suborder.sub_total,
    grand_total: suborder.grand_total,
    items: suborder.items,
    shipment_status: suborder.shipment.status,
    carrier: suborder.shipment.carrier,
    tracking_number: suborder.shipment.tracking_number,
    shipped_at: suborder.shipment.shipped_at,
    delivered_at: suborder.shipment.delivered_at,
  };
}

function toMarketplaceOrderDetail(order: MockMarketplaceOrder): PlatformMarketplaceOrderDetail {
  return {
    id: order.id,
    contact_name: order.contact_name,
    contact_email: order.contact_email,
    contact_phone: order.contact_phone,
    address_line1: order.address_line1,
    address_line2: order.address_line2,
    city: order.city,
    region: order.region,
    postal_code: order.postal_code,
    country: order.country,
    grand_total: order.grand_total,
    currency: order.currency,
    created_at: order.created_at,
    status: computeRollupStatus(order.suborders.map((s) => s.shipment.status)),
    suborders: order.suborders.map(toSuborderDetail),
  };
}

function requireMarketplaceOrder(orderId: string): MockMarketplaceOrder {
  const order = marketplaceOrders.get(orderId);
  if (!order) throw new Error(`No such marketplace order: ${orderId}`);
  return order;
}

/** MFA challenges awaiting `verifyMfa`, keyed by `challenge_id`. */
const pendingChallenges = new Map<string, { operator: OperatorRecord; code: string; expiresAt: number }>();

const MFA_CHALLENGE_TTL_MS = 5 * 60_000;
const SESSION_TTL_MS = 30 * 60_000;

/**
 * Synthesizes the REAL 6-step checklist shape (`LiveOnboardingChecklist`) from this mock's
 * fixture fields — computed on every call rather than stored, same "no drift" discipline
 * the real `onboarding_service.compute_onboarding_checklist` uses. The mock has no
 * per-product-image/per-sale-source/logo tracking of its own, so each step reuses the
 * nearest existing fixture signal as an honest stand-in (documented per step below) rather
 * than inventing new bookkeeping just for this demo layer:
 * - products/POS sales reuse the legacy 3-step `checklist.first_product_added`/
 *   `first_sale_recorded` booleans (all-or-nothing against each step's real target).
 * - online sales and branding have no fixture field at all, so they're inferred from
 *   `usage.active_users` and `plan_code`/`is_demo` respectively — a reasonably-onboarded
 *   business (several staff, on a paid plan) reads as having done both.
 * - staff uses the one signal the mock genuinely tracks, `usage.active_users`.
 */
function deriveOnboardingSteps(detail: MockTenantDetail): LiveOnboardingChecklistStep[] {
  const productsCurrent = detail.checklist.first_product_added ? 10 : 0;
  const posSalesCurrent = detail.checklist.first_sale_recorded ? 5 : 0;
  const isEstablished = detail.checklist.first_sale_recorded && detail.usage.active_users >= 5;
  const onlineSalesCurrent = isEstablished ? 3 : 0;
  const hasBranding = detail.plan_code !== "TRIAL" && !detail.is_demo;
  const extraStaffCurrent = Math.max(detail.usage.active_users - 1, 0);

  return [
    {
      step: "BUSINESS_CREATED",
      label: "Business created",
      completed: detail.usage.branch_count > 0,
      completed_at: detail.usage.branch_count > 0 ? detail.checklist.updated_at : null,
      current: null,
      target: null,
    },
    {
      step: "PRODUCTS_STOCKED",
      label: "Add 10 products, each with a photo",
      completed: productsCurrent >= 10,
      completed_at: productsCurrent >= 10 ? detail.checklist.updated_at : null,
      current: productsCurrent,
      target: 10,
    },
    {
      step: "POS_SALES_MADE",
      label: "Make 5 sales at the till",
      completed: posSalesCurrent >= 5,
      completed_at: posSalesCurrent >= 5 ? detail.checklist.updated_at : null,
      current: posSalesCurrent,
      target: 5,
    },
    {
      step: "ONLINE_SALES_MADE",
      label: "Get 3 online orders",
      completed: onlineSalesCurrent >= 3,
      completed_at: onlineSalesCurrent >= 3 ? detail.checklist.updated_at : null,
      current: onlineSalesCurrent,
      target: 3,
    },
    {
      step: "BRANDING_CUSTOMIZED",
      label: "Add your logo and branding",
      completed: hasBranding,
      completed_at: hasBranding ? detail.checklist.updated_at : null,
      current: null,
      target: null,
    },
    {
      step: "STAFF_ADDED",
      label: "Add a staff member",
      completed: extraStaffCurrent >= 1,
      completed_at: extraStaffCurrent >= 1 ? detail.checklist.updated_at : null,
      current: extraStaffCurrent,
      target: 1,
    },
  ];
}

function isOnboardingComplete(detail: MockTenantDetail): boolean {
  return deriveOnboardingSteps(detail).every((s) => s.completed);
}

function toSummary(detail: MockTenantDetail): PlatformTenantSummary {
  const { usage: _usage, checklist: _checklist, ...summary } = detail;
  return { ...summary, onboarding_complete: isOnboardingComplete(detail) };
}

function toDetail(detail: MockTenantDetail): PlatformTenantDetail {
  return { ...detail, onboarding_complete: isOnboardingComplete(detail) };
}

function requireTenant(tenantId: string): MockTenantDetail {
  const tenant = tenants.get(tenantId);
  if (!tenant) throw new Error(`No such tenant: ${tenantId}`);
  return tenant;
}

function recordAudit(operatorEmail: string, action: string, targetTenantId: string | null, detail: string | null) {
  const operator = OPERATORS.find((o) => o.email === operatorEmail);
  auditLog.unshift({
    id: newId(),
    operator_id: operator?.id ?? "unknown",
    operator_email: operatorEmail,
    action,
    target_tenant_id: targetTenantId,
    timestamp: new Date().toISOString(),
    detail,
  });
}

// The mock "current operator" for audit attribution — set on successful verifyMfa, since
// this module has no React context of its own. `PlatformAuthContext` is the source of
// truth for the UI; this is purely so mutating calls (suspend/reactivate/backup/onboard)
// have *something* real to attribute in the audit log without threading the operator
// through every method signature.
let currentOperatorEmail: string | null = null;

// ---------------------------------------------------------------------------------
// Static/mocked log fixtures (§9.4) — structured entries carrying the correlation keys
// §9.4 names explicitly: event_id, tenant_id, device_id, request_id.
// ---------------------------------------------------------------------------------

const logFixtures: LogEntry[] = [
  {
    event_id: newId(),
    tenant_id: TENANT_IDS.acme,
    device_id: "till-acme-branch2-04",
    request_id: newId(),
    timestamp: minutesAgo(3),
    level: "error",
    message: "sync_upstream: STOCK_ADJUST rejected — NEGATIVE_STOCK guard tripped for item 7f2c…",
  },
  {
    event_id: newId(),
    tenant_id: TENANT_IDS.bluewave,
    device_id: "till-bluewave-hq-11",
    request_id: newId(),
    timestamp: minutesAgo(8),
    level: "warn",
    message: "POST /sales returned 409 — insufficient stock for line item, till retried with adjusted cart",
  },
  {
    event_id: newId(),
    tenant_id: TENANT_IDS.everline,
    device_id: null,
    request_id: newId(),
    timestamp: hoursAgo(1),
    level: "error",
    message: "sync_upstream: MALFORMED_EVENT — payload missing required field 'branch_id'",
  },
  {
    event_id: newId(),
    tenant_id: TENANT_IDS.citrus,
    device_id: "till-citrus-01",
    request_id: newId(),
    timestamp: hoursAgo(5),
    level: "info",
    message: "auth: refresh token rotated",
  },
  {
    event_id: newId(),
    tenant_id: null,
    device_id: null,
    request_id: newId(),
    timestamp: minutesAgo(1),
    level: "info",
    message: "readyz: DB connectivity check passed",
  },
  {
    event_id: newId(),
    tenant_id: TENANT_IDS.desert,
    device_id: "till-desert-01",
    request_id: newId(),
    timestamp: daysAgo(1),
    level: "debug",
    message: "sync_downstream: 3 inventory_changes, 0 conflict_outcomes returned",
  },
  {
    event_id: newId(),
    tenant_id: TENANT_IDS.acme,
    device_id: "till-acme-branch1-02",
    request_id: newId(),
    timestamp: hoursAgo(11),
    level: "warn",
    message: "image upload: MinIO PutObject latency 2.4s (above 1s budget)",
  },
  {
    event_id: newId(),
    tenant_id: TENANT_IDS.bluewave,
    device_id: null,
    request_id: newId(),
    timestamp: daysAgo(2),
    level: "error",
    message: "payments/webhook: signature verification not implemented for provider 'MTN_MOMO' (TODO)",
  },
];

// ---------------------------------------------------------------------------------
// The client
// ---------------------------------------------------------------------------------

export const mockPlatformApiClient: PlatformApiClient = {
  async requestLogin(email, password) {
    await delay();
    const operator = OPERATORS.find((o) => o.email === email && o.password === password);
    if (!operator) throw new Error("Invalid email or password.");
    const challengeId = newId();
    // Demo-only: a real MFA implementation never generates a "known" code delivered back
    // to the requester (see MfaChallenge.demo_code's doc comment in types.ts).
    const code = String(Math.floor(100000 + Math.random() * 900000));
    pendingChallenges.set(challengeId, { operator, code, expiresAt: Date.now() + MFA_CHALLENGE_TTL_MS });
    const challenge: MfaChallenge = { challenge_id: challengeId, demo_code: code };
    return challenge;
  },

  async verifyMfa(challengeId, code) {
    await delay();
    const pending = pendingChallenges.get(challengeId);
    if (!pending || pending.expiresAt < Date.now()) {
      pendingChallenges.delete(challengeId);
      throw new Error("MFA challenge expired or not found — start over.");
    }
    if (pending.code !== code.trim()) throw new Error("Incorrect verification code.");
    pendingChallenges.delete(challengeId);
    currentOperatorEmail = pending.operator.email;
    const session: PlatformSession = {
      operator: {
        id: pending.operator.id,
        email: pending.operator.email,
        display_name: pending.operator.display_name,
        mfa_enrolled: pending.operator.mfa_enrolled,
      },
      access_token: `mock-platform-token.${newId()}`,
      expires_at: new Date(Date.now() + SESSION_TTL_MS).toISOString(),
    };
    recordAudit(session.operator.email, "OPERATOR_LOGIN", null, null);
    return session;
  },

  async logout(session) {
    await delay(40);
    recordAudit(session.operator.email, "OPERATOR_LOGOUT", null, null);
    currentOperatorEmail = null;
  },

  /**
   * Mirrors the real `POST /platform/tenants`'s 2026-07-13 shape: no `owner_password`
   * collected or stored, a one-time simulated `setup_token` returned instead (this mock has
   * no real credential-setup-token table, so it's an opaque random string, not a decodable
   * JWT — clearly labeled as such in `note`) — see `TenantCreationResult`'s doc comment in
   * `api/types.ts`.
   */
  async createTenant(input): Promise<TenantCreationResult> {
    await delay(250);
    if (!input.business_name.trim()) throw new Error("Business name is required.");
    if (!input.owner_email.trim()) throw new Error("Owner email is required.");
    const id = newId();
    const ownerUserId = newId();
    const detail: MockTenantDetail = {
      id,
      business_name: input.business_name,
      business_type: input.business_type,
      currency: input.currency,
      is_suspended: false,
      is_demo: false,
      plan_code: input.plan_code,
      created_at: new Date().toISOString(),
      last_transaction_at: null,
      turnover_all_time: asDecimalString("0"),
      turnover_last_30d: asDecimalString("0"),
      usage: {
        branch_count: 0,
        active_users: 1,
        storage_used_bytes: 0,
        storage_quota_bytes: input.storage_quota_gb * GB,
        sync_events_last_30d: 0,
      },
      checklist: {
        tenant_id: id,
        first_branch_created: false,
        first_product_added: false,
        first_sale_recorded: false,
        updated_at: new Date().toISOString(),
      },
    };
    tenants.set(id, detail);
    recordAudit(
      currentOperatorEmail ?? "unknown",
      "TENANT_CREATED",
      id,
      `plan=${input.plan_code}, owner=${input.owner_email}`,
    );
    return {
      tenant: toDetail(detail),
      owner_user_id: ownerUserId,
      owner_email: input.owner_email,
      setup_token: `mock-setup-token.${newId()}`,
      setup_token_expires_at: new Date(Date.now() + 72 * 60 * 60_000).toISOString(),
      note:
        "Mock mode: no real backend invite exists — this is a simulated one-time setup token for " +
        "demoing the flow, not a real credential-setup link.",
    };
  },

  async getOnboardingChecklist(tenantId) {
    await delay(60);
    return requireTenant(tenantId).checklist;
  },

  async advanceOnboardingStep(tenantId, step): Promise<OnboardingChecklist> {
    await delay(120);
    const tenant = requireTenant(tenantId);
    tenant.checklist = { ...tenant.checklist, [step]: true, updated_at: new Date().toISOString() };
    if (step === "first_branch_created") tenant.usage.branch_count = Math.max(1, tenant.usage.branch_count);
    recordAudit(currentOperatorEmail ?? "unknown", "ONBOARDING_STEP_ADVANCED", tenantId, step);
    return tenant.checklist;
  },

  /**
   * Synthesizes the REAL 6-step checklist shape (`LiveOnboardingChecklist`) — see
   * `deriveOnboardingSteps`'s doc comment for exactly which fixture signal backs each of
   * the 6 steps in mock mode.
   */
  async getTenantChecklist(tenantId): Promise<LiveOnboardingChecklist> {
    await delay(80);
    const tenant = requireTenant(tenantId);
    const steps = deriveOnboardingSteps(tenant);
    return {
      tenant_id: tenantId,
      steps,
      all_complete: steps.every((s) => s.completed),
    };
  },

  async listHealthNodes() {
    await delay(150);
    return buildMockHealthNodes(syncConflicts.length);
  },

  async searchLogs(filter: LogFilter) {
    await delay(150);
    return logFixtures.filter((entry) => {
      if (filter.tenantId && entry.tenant_id !== filter.tenantId) return false;
      if (filter.level && entry.level !== filter.level) return false;
      if (filter.since && entry.timestamp < filter.since) return false;
      if (filter.query) {
        const needle = filter.query.trim().toLowerCase();
        if (needle.length > 0 && !entry.message.toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  },

  async listSyncConflicts() {
    await delay(150);
    return [...syncConflicts];
  },

  async listPendingReconciliations() {
    await delay(150);
    return [...pendingReconciliations];
  },

  async getQueueAlertSummary(): Promise<QueueAlertSummary> {
    await delay(150);
    return {
      threshold_minutes: DEFAULT_STALE_THRESHOLD_MS / 60_000,
      sync_conflicts_stale_count: countAgedBeyondThreshold(syncConflicts),
      payment_pending_stale_count: countAgedBeyondThreshold(pendingReconciliations),
    };
  },

  async listStorageQuotaWarnings() {
    await delay(150);
    const warnings: StorageQuotaWarning[] = [];
    for (const tenant of tenants.values()) {
      const ratio = tenant.usage.storage_used_bytes / tenant.usage.storage_quota_bytes;
      if (ratio >= 0.8) {
        warnings.push({
          tenant_id: tenant.id,
          tenant_name: tenant.business_name,
          storage_used_bytes: tenant.usage.storage_used_bytes,
          storage_quota_bytes: tenant.usage.storage_quota_bytes,
        });
      }
    }
    return warnings;
  },

  async listTenants() {
    await delay(150);
    return [...tenants.values()].map(toSummary);
  },

  async getTenant(tenantId) {
    await delay(120);
    return toDetail(requireTenant(tenantId));
  },

  async suspendTenant(tenantId, reason) {
    await delay(200);
    const tenant = requireTenant(tenantId);
    tenant.is_suspended = true;
    recordAudit(currentOperatorEmail ?? "unknown", "TENANT_SUSPENDED", tenantId, reason || null);
    return toDetail(tenant);
  },

  async reactivateTenant(tenantId) {
    await delay(200);
    const tenant = requireTenant(tenantId);
    tenant.is_suspended = false;
    recordAudit(currentOperatorEmail ?? "unknown", "TENANT_REACTIVATED", tenantId, null);
    return toDetail(tenant);
  },

  async triggerBackup(tenantId) {
    await delay(300);
    requireTenant(tenantId);
    const trigger: BackupTrigger = {
      backup_id: newId(),
      tenant_id: tenantId,
      triggered_at: new Date().toISOString(),
      status: "QUEUED",
    };
    recordAudit(currentOperatorEmail ?? "unknown", "BACKUP_TRIGGERED", tenantId, `backup_id=${trigger.backup_id}`);
    return trigger;
  },

  async resetDemoData(tenantId) {
    await delay(300);
    const tenant = requireTenant(tenantId);
    // Mirrors the real backend's own guard (`backend/app/api/v1/platform/tenants.py::
    // reset_demo_data`) rather than silently no-opping — a caller (or a future page) that
    // sends a non-demo tenant id should see the same failure shape in mock mode as it
    // would against the real API.
    if (!tenant.is_demo) throw new Error("This tenant is not the demo tenant");
    const result: DemoResetResult = { tenant_id: tenantId, reset_at: new Date().toISOString() };
    recordAudit(currentOperatorEmail ?? "unknown", "DEMO_TENANT_RESET", tenantId, `reset_at=${result.reset_at}`);
    return result;
  },

  async listAuditLog(tenantId) {
    await delay(100);
    if (!tenantId) return [...auditLog];
    return auditLog.filter((entry) => entry.target_tenant_id === tenantId);
  },

  /**
   * Aggregates real numbers from this mock's own fixture store — not independent fake
   * numbers, so the Overview page's totals are always internally consistent with what
   * `listTenants()`/`getTenant()` show elsewhere in mock mode. `sales_count_last_30d`/
   * `sales_revenue_last_30d` have no dedicated sales fixture in this mock client, so
   * they're derived deterministically from `sync_events_last_30d` (a rough, clearly
   * approximated stand-in, not a claim of real sales data) — documented here rather than
   * silently presented as precise.
   */
  async getPlatformStats(): Promise<PlatformStats> {
    await delay(120);
    const all = [...tenants.values()];
    const totalSyncEvents = all.reduce((sum, t) => sum + t.usage.sync_events_last_30d, 0);
    const approxSalesCount = Math.round(totalSyncEvents * 0.12);
    const approxRevenue = approxSalesCount * 24500; // plausible average basket size, UGX-scale
    return {
      total_tenants: all.length,
      active_tenants: all.filter((t) => !t.is_suspended).length,
      suspended_tenants: all.filter((t) => t.is_suspended).length,
      total_branches: all.reduce((sum, t) => sum + t.usage.branch_count, 0),
      total_users: all.reduce((sum, t) => sum + t.usage.active_users, 0),
      sales_count_last_30d: approxSalesCount,
      sales_revenue_last_30d: asDecimalString(approxRevenue.toFixed(2)),
    };
  },

  /**
   * Simulates §9.1's real impersonation session shape (short-lived, non-refreshable,
   * audit-logged) without a real tenant-realm token — this mock's `access_token` is an
   * opaque string, never a real JWT usable against the real backend's `/auth/me`, unlike
   * `realPlatformApiClient`'s (see `ImpersonationViewPage.tsx`, which gates its real
   * `/auth/me` call behind `PLATFORM_API_MODE !== "mock"` for exactly this reason).
   */
  async impersonateTenant(tenantId, userId, reason): Promise<ImpersonationSession> {
    await delay(180);
    const tenant = requireTenant(tenantId);
    const impersonatedUserId = userId ?? newId();
    const domain = tenant.business_name.toLowerCase().replace(/[^a-z0-9]+/g, "") || "tenant";
    const session: ImpersonationSession = {
      access_token: `mock-impersonation-token.${newId()}`,
      token_type: "bearer",
      expires_at: new Date(Date.now() + 15 * 60_000).toISOString(),
      impersonated_user_id: impersonatedUserId,
      impersonated_user_email: `owner@${domain}.example`,
      tenant_id: tenantId,
      // Same honesty rule as access_token above: never issued by the real
      // backend, so `POST /auth/impersonation/consume` would reject it —
      // `TenantDetailPage` only opens the dashboard-admin handoff tab in real mode.
      dashboard_handoff_code: `mock-handoff-code.${newId()}`,
    };
    recordAudit(
      currentOperatorEmail ?? "unknown",
      "TENANT_IMPERSONATION_ISSUED",
      tenantId,
      reason ? `reason=${reason}` : "no reason given",
    );
    return session;
  },

  async listMarketplaceOrders(filters: MarketplaceOrderFilters = {}): Promise<PlatformMarketplaceOrderSummary[]> {
    await delay(150);
    let rows = [...marketplaceOrders.values()]
      .map(toMarketplaceOrderSummary)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    if (filters.status) rows = rows.filter((row) => row.status === filters.status);
    if (filters.since) rows = rows.filter((row) => row.created_at >= filters.since!);
    if (filters.until) rows = rows.filter((row) => row.created_at <= filters.until!);
    if (filters.limit) rows = rows.slice(0, filters.limit);
    return rows;
  },

  async getMarketplaceOrder(orderId): Promise<PlatformMarketplaceOrderDetail> {
    await delay(120);
    return toMarketplaceOrderDetail(requireMarketplaceOrder(orderId));
  },

  /**
   * Mirrors the real route's exact behavior: writes straight to the mock's own
   * `shipment` stand-in (never a parallel status field on the suborder itself), validates
   * `status` transitions against the same `VALID_SHIPMENT_TRANSITIONS` graph the real
   * backend enforces (throwing the identical "Cannot transition shipment from X to Y"
   * message `shipping_service.validate_transition` raises, so `MarketplaceOrderDetailPage`
   * surfaces the same copy regardless of which client is active), and records an audit
   * entry (`MARKETPLACE_SUBORDER_STATUS_UPDATED`), matching the real route's §9.1 audit
   * requirement.
   */
  async updateMarketplaceSuborderStatus(
    orderId,
    suborderId,
    body: MarketplaceSuborderStatusUpdateInput,
  ): Promise<PlatformMarketplaceSuborderDetail> {
    await delay(200);
    const order = requireMarketplaceOrder(orderId);
    const suborder = order.suborders.find((s) => s.suborder_id === suborderId);
    if (!suborder) throw new Error(`No such suborder: ${suborderId}`);

    if (body.carrier !== undefined) suborder.shipment.carrier = body.carrier;
    if (body.tracking_number !== undefined) suborder.shipment.tracking_number = body.tracking_number;
    if (body.status !== undefined && body.status !== suborder.shipment.status) {
      const allowed = VALID_SHIPMENT_TRANSITIONS[suborder.shipment.status];
      if (!allowed.has(body.status)) {
        throw new Error(`Cannot transition shipment from ${suborder.shipment.status} to ${body.status}`);
      }
      suborder.shipment.status = body.status;
      if (body.status === "SHIPPED") suborder.shipment.shipped_at = new Date().toISOString();
      if (body.status === "DELIVERED") suborder.shipment.delivered_at = new Date().toISOString();
    }

    recordAudit(
      currentOperatorEmail ?? "unknown",
      "MARKETPLACE_SUBORDER_STATUS_UPDATED",
      suborder.tenant_id,
      `suborder=${suborderId}`,
    );
    return toSuborderDetail(suborder);
  },

  async getGeneralSettings(): Promise<PlatformGeneralSettings> {
    await delay(80);
    const { smtp_password, ...rest } = generalSettings;
    return { ...rest, smtp_password_set: Boolean(smtp_password) };
  },

  async updateGeneralSettings(body: PlatformGeneralSettingsUpdate): Promise<PlatformGeneralSettings> {
    await delay(150);
    if (body.smtp_host !== undefined) generalSettings.smtp_host = body.smtp_host;
    if (body.smtp_port !== undefined) generalSettings.smtp_port = body.smtp_port;
    if (body.smtp_username !== undefined) generalSettings.smtp_username = body.smtp_username;
    if (body.smtp_password !== undefined) generalSettings.smtp_password = body.smtp_password || null;
    if (body.smtp_from_email !== undefined) generalSettings.smtp_from_email = body.smtp_from_email;
    if (body.smtp_from_name !== undefined) generalSettings.smtp_from_name = body.smtp_from_name;
    if (body.smtp_use_tls !== undefined) generalSettings.smtp_use_tls = body.smtp_use_tls;

    recordAudit(currentOperatorEmail ?? "unknown", "PLATFORM_SETTINGS_UPDATED", null, "General Settings");

    const { smtp_password, ...rest } = generalSettings;
    return { ...rest, smtp_password_set: Boolean(smtp_password) };
  },

  /** Mirrors the real backend's "always resolves, never throws" contract — `sent: false`
   * with a plain-English reason when nothing's configured yet, since there's no real SMTP
   * server to actually reach in mock mode. */
  async sendTestEmail(to: string): Promise<SendTestEmailResult> {
    await delay(300);
    if (!generalSettings.smtp_host || !generalSettings.smtp_port || !generalSettings.smtp_from_email) {
      return {
        sent: false,
        detail: "SMTP is not configured — set host, port, and a from-address in General Settings before sending email.",
      };
    }
    return { sent: true, detail: `Test email sent to ${to}. (mock mode — no real email was sent)` };
  },
};

/** Test-only accessor — lets `mockPlatformApiClient.test.ts` assert against the demo
 * operator directory's shape without hardcoding credentials in two places. Not imported
 * by any page component. */
export const __testOnly = { OPERATORS, TENANT_IDS, MARKETPLACE_ORDER_IDS };
